import {
  makeAutoObservable,
  observable,
  reaction,
  action,
  isFlowCancellationError,
} from "mobx";
import { CancellablePromise } from "mobx/dist/internal";
import { notifications } from "@mantine/notifications";
import { Pager } from "../apiResponses/paging";
import { STATUS_CODES } from "http";
import { duration } from "moment";

const DEFAULT_PAGE_SIZE = 50;

export enum LoadState {
  // The request has never been triggered and completed
  NotRequested = "NotRequested",
  // A request is in-flight.
  Refreshing = "Refreshing",
  // The data is fully loaded and up-to-date
  UpToDate = "UpToDate",
  // Data has been loaded, but it is now stale due to changed parameters of the request
  Stale = "Stale",
  // The most recent request errored. There is stale data from a previous request.
  Errored = "Errored",
  // The data is fully loaded and up-to-date, but a new page of data is being fetched
  Paging = "Paging",
}

export namespace LoadState {
  export function shouldTriggerRequest(s: LoadState): boolean {
    return [
      LoadState.NotRequested,
      LoadState.Stale,
      LoadState.Errored,
    ].includes(s);
  }

  export function shouldShowLoading(s: LoadState): boolean {
    return ![LoadState.UpToDate].includes(s);
  }
}

interface GeneralOptions<T> {
  // A name used for identifying debug logs belonging to this auto request
  name?: string;
  // A name used to identify this auto request to customers in error popups
  displayName?: string;
  // Whether or not to print out debug logs
  debug?: boolean;
  // Whether to silence request errors
  silentErrors?: boolean;
  // How many milliseconds to allow a request to live before considering it stale
  //
  // The default is 12 hours. Set to 0 to disable this behavior.
  dataValidityPeriod?: number;
  // A function to combine multiple instances of `DataType` into one. This can be used when multiple
  // requests fetch different parts of your data. This function will be called only when data is
  // reported while the autoRequest is up to date.
  foldData?: (existing: T, incoming: T) => T;
}

interface PagingOptions {
  supportPaging?: boolean;
  pageSize?: number;
}

function isPagingOptions<T>(
  s: Options<any> | undefined,
): s is PagingOptions & GeneralOptions<T> {
  if (s === undefined) {
    return false;
  }
  return "supportPaging" in s;
}

type Options<T> = T extends Pager
  ? GeneralOptions<T> & PagingOptions
  : GeneralOptions<T>;

/* 
AutoRequest is used to assist in automatically fetching up-to-date data based on a predefined
list of request dependencies, with built-in exponentially backed off retrying on error.

# Proper Use

## Store
1. Add an AutoRequest as a field in the store, generic over your response type.
2. Write your request flow normally, and at the end call `reportData` with the JSON response, or
    `reportError` if an error occurred.
3. Write a method that returns a list of observables that should trigger a re-fetch when they change
4. (Optional) Write a method that determines _when_ the fetch should happen. This can depend on
     observables that should not invalidate the data, like various UI state.
5. Pass the request flow and shouldFetch into the AutoRequest constructor as arrow functions.
6. In the store initialization, initialize the autoRequest with the dependencies

## Component

The data is fetched lazily. In order for the data to be fetched, it muust be requested through the
`getData` method or manually triggered with `triggerRequest`.

Whenever you get the data from the autoRequest, it will automatically fetch new data if it is stale.

It is also possible to get stale data, if it is available. In that case, new data will not be
fetched until manually triggered with `triggerRequest`.

## Paging

If you want to use paging with an AutoRequest, it will handle the paging. It is necessary for the
request to accept optional offset and pageSize arguments in the fetch request.

## Multiple Requests

In some cases, you want to combine multiple data sources into the same autoRequest.
Consider the case where you want to fetch data from endpoints A and B under the same conditions.
You can report data multiple times by implementing `foldData` in the options.

Example:
```typescript
interface Data {
  appleCount?: number,
  bananaCount?: number,
}

function foldData(existing: Data, incoming: Data): Data {
  return {
    appleCount: existing.appleCount ?? incoming.appleCount,
    bananaCount: existing.bananaCount ?? incoming.bananaCount,
  }
}

function makeRequest() {
  ...
  const apples = makeAppleRequest();
  const appleData = {
    appleCount: apples,
  };
  this.appleBananaReq.reportData(appleData);
  ...
  const bananas = makeBananaRequest();
  const bananaData = {
    bananaCount: bananas,
  };
  this.appleBananaReq.reportData(bananaData);
}
```
*/
class AutoRequest<DataType> {
  private _data: DataType | null;
  _fetchTime: Date | null;
  // TODO: Store deps that generated _data next to the data? That way stale data can be reasoned about better

  state: LoadState;
  abortController: AbortController;
  requestHandle?: CancellablePromise<any>;
  makeRequest: (
    offset?: number,
    pageSize?: number,
    requestId?: number,
  ) => CancellablePromise<any>;
  shouldMakeRequest: () => boolean;

  // The number of milliseconds to wait before the next
  nextErrorBackoff: number;
  errorBackoffHandle?: NodeJS.Timeout;
  lastDataRequestedTime?: Date;

  requestCount: number;

  stalenessDebounce: () => void;

  options?: Options<DataType>;

  constructor(
    makeRequest: (
      offset?: number,
      pageSize?: number,
    ) => CancellablePromise<any>,
    shouldMakeRequest?: () => boolean,
    options?: Options<DataType>,
  ) {
    this.state = LoadState.NotRequested;
    this._data = null;
    this._fetchTime = null;
    this.requestCount = 0;
    this.abortController = new AbortController();
    this.makeRequest = makeRequest;
    this.shouldMakeRequest = shouldMakeRequest ?? (() => true);
    this.options = options;

    if (this.options?.dataValidityPeriod === undefined) {
      this.stalenessDebounce = createDebounce(
        () => this.markInvalid(),
        duration(12, "hours").asMilliseconds(),
      );
    } else if (this.options.dataValidityPeriod > 0) {
      this.stalenessDebounce = createDebounce(
        () => this.markInvalid(),
        this.options.dataValidityPeriod,
      );
    } else {
      this.stalenessDebounce = () => {};
    }

    this.nextErrorBackoff = 500;

    this.debugLog(`Instantiating auto request`);

    makeAutoObservable<AutoRequest<DataType>, "_data">(this, {
      _data: observable,
      triggerRequest: action,
      markDataRequested: action,
      markInvalid: action,
    });
  }

  Initialize(data: () => any) {
    this.debugLog(`Initializing auto request`);
    if (this.supportsPaging) {
      reaction(
        () => [...data(), this.pageSize],
        () => this.markInvalid(),
      );
    } else {
      reaction(
        () => [...data()],
        () => this.markInvalid(),
      );
    }
    this.debug &&
      reaction(
        () => this.state,
        () => this.debugLog(`current state: ${this.state}`),
      );
  }

  // Trigger a request being sent, if a new request is necessary.
  // This should _always_ be called before using the data, if you want the data to be up-to-date
  triggerRequest() {
    this.debugLog(`request triggered`);
    this.debugLog(`current state: ${this.state}`);
    if (
      this.shouldMakeRequest() &&
      LoadState.shouldTriggerRequest(this.state)
    ) {
      if (
        this.state === LoadState.Errored &&
        this.errorBackoffHandle !== undefined
      ) {
        this.debugLog(`aborting request due to error backoff in progress`);
        return;
      }
      this.debugLog(`firing request`);
      this.state = LoadState.Refreshing;
      this.requestCount += 1;
      this.requestHandle = this.makeRequest(
        undefined,
        undefined,
        this.requestCount,
      );
      // For some reason the dev tools is crashing here...
      this.requestHandle.catch((e) => {
        if (isFlowCancellationError(e)) {
          this.debugLog(`caught cancellation`);
        } else {
          throw e;
        }
      });
    }
  }

  markDataRequested() {
    this.lastDataRequestedTime = new Date();
  }

  reportData(data: DataType, requestId?: number) {
    if (
      this.state === LoadState.UpToDate &&
      this.options?.foldData !== undefined &&
      this._data !== null
    ) {
      if (requestId !== undefined && requestId !== this.requestCount) {
        // We somehow failed to cancel this request
        return;
      }
      this._data = this.options.foldData(this._data, data);
    } else {
      this._data = data;
    }
    this.state = LoadState.UpToDate;
    this._fetchTime = new Date();
    this.nextErrorBackoff = 500;
    this.stalenessDebounce();
  }

  reportError(statusCode?: number) {
    this.state = LoadState.Errored;
    this.nextErrorBackoff *= 2;
    this.debugLog("Options", this.options);
    if (
      !this.options?.silentErrors &&
      this.lastDataRequestedTime !== undefined &&
      new Date().valueOf() - this.lastDataRequestedTime.valueOf() < 8_000
    ) {
      switch (statusCode) {
        case 401:
          // In this case, we want to only retry once. We do this by setting the backoff very,
          // very high.
          if (this.nextErrorBackoff < 2000) {
            this.showDefaultErrorMessage();
          } else {
            notifications.show({
              title: "Unauthorized to view content!",
              message:
                "Please try refreshing the page. If this problem continues, contact support",
              color: "red",
              autoClose: false,
              withCloseButton: false,
            });
            // This means we functionally never retry
            this.nextErrorBackoff = 24 * 60 * 60 * 1000;
          }
          break;
        default:
          this.showDefaultErrorMessage();
          break;
      }
    }
    this.debugLog(`error backoff for ${this.nextErrorBackoff}ms`);
    this.errorBackoffHandle = setTimeout(
      action(() => {
        this.debugLog(`finished error backoff. Firing new request.`);
        this.errorBackoffHandle = undefined;
        this.triggerRequest();
      }),
      this.nextErrorBackoff,
    );
  }

  showDefaultErrorMessage() {
    notifications.show({
      title: this.options?.displayName
        ? `Error fetching ${this.options.displayName}.`
        : "Request error!",
      message: `Retrying in ${this.nextErrorBackoff / 1000} seconds.`,
      color: "red",
      autoClose: this.nextErrorBackoff,
    });
  }

  markInvalid(): void {
    this.debugLog(`marking invalid`);
    // TODO(Nate): What exactly to do after error? Should really depend on the type and class of
    // error
    switch (this.state) {
      case LoadState.Paging:
      case LoadState.Refreshing:
        this.requestHandle?.cancel();
        this.abortController.abort();
        this.abortController = new AbortController();
        this.state =
          this._data !== null ? LoadState.Stale : LoadState.NotRequested;
        return;
      case LoadState.UpToDate:
        this.state = LoadState.Stale;
        return;
    }
  }

  getData(acceptStale?: boolean): DataType | null {
    this.debugLog(`getting data`);
    this.markDataRequested();
    if (this.state === LoadState.UpToDate) {
      return this._data;
    }

    if (acceptStale && this.hasStaleData) {
      return this._data;
    }

    // We need to call this here, because observable access isn't recorded inside of actions
    if (this.shouldMakeRequest()) {
      this.triggerRequest();
    }

    return null;
  }

  requestNewPage(pageNum: number) {
    this.debugLog(`paging request triggered for page ${pageNum}`);
    if (
      pageNum < 1 ||
      (this.totalPages !== null && pageNum > this.totalPages)
    ) {
      console.error(
        `Bad page number requested. Requested ${pageNum} when there are ${this.totalPages} total.`,
      );
      return;
    }

    // NOTE: Currently, a user cannot request for a new page until the currently requested page finishes loading.
    if (this.shouldMakeRequest() && this.state === LoadState.UpToDate) {
      this.debugLog("firing paging request");
      this.state = LoadState.Paging;
      this.requestHandle = this.makeRequest(
        (pageNum - 1) * this.pageSize,
        this.pageSize,
      );
      this.requestHandle.catch((e) => {
        if (isFlowCancellationError(e)) {
          this.debugLog("caught cancellation");
        } else {
          this.reportError();
        }
      });
    }
  }

  requestNextPage() {
    if (this.currentPage === null || this.currentPage === this.totalPages) {
      return;
    }
    this.requestNewPage(this.currentPage + 1);
  }

  requestPreviousPage() {
    if (this.currentPage === null || this.currentPage === 1) {
      return;
    }
    this.requestNewPage(this.currentPage - 1);
  }

  debugLog(message?: any, ...optionalParams: any[]) {
    this.debug && console.log(`${this.name}: ${message}`, ...optionalParams);
  }

  get hasStaleData(): boolean {
    return this._data !== null && this.state !== LoadState.UpToDate;
  }

  get fetchTime(): Date | null {
    if (LoadState.shouldShowLoading(this.state)) {
      return null;
    }
    return this._fetchTime;
  }

  get debug(): boolean {
    return this.options?.debug ?? false;
  }

  get name(): string {
    return this.options?.name ?? "Unnamed AutoRequest";
  }

  get signal(): AbortSignal {
    return this.abortController.signal;
  }

  get supportsPaging(): boolean {
    return isPagingOptions(this.options);
  }

  set pageSize(size: number) {
    if (size <= 0) {
      return;
    }

    if (!isPagingOptions(this.options)) {
      return;
    }

    this.options.pageSize = size;
  }

  // Returns 0 if paging is not implemented
  get pageSize(): number {
    if (!isPagingOptions(this.options)) {
      return 0;
    }

    return this.options.pageSize ?? DEFAULT_PAGE_SIZE;
  }

  // Get the 1-indexed page out of the total number of pages
  get currentPage(): number | null {
    if (!isPagingOptions(this.options) || this._data === null) {
      return null;
    }

    // Typescript can't figure out this conditional typing on its own
    const paging = (this._data as Pager).paging;
    const pageIdx = Math.floor(paging.offset / this.pageSize!);
    return pageIdx + 1;
  }

  get totalPages(): number | null {
    if (!isPagingOptions(this.options) || this._data === null) {
      return null;
    }

    const paging = (this._data as Pager).paging;
    return Math.ceil(paging.total / this.pageSize!);
  }

  get hasNextPage(): boolean {
    if (this.currentPage === null || this.totalPages === null) {
      return false;
    }

    return this.currentPage !== this.totalPages;
  }

  get hasPreviousPage(): boolean {
    if (this.currentPage === null) {
      return false;
    }

    return this.currentPage > 1;
  }

  get totalRecords(): number | null {
    if (!isPagingOptions(this.options) || this._data === null) {
      return null;
    }

    return (this._data as Pager).paging.total;
  }
}

function createDebounce(f: () => void, timeout: number): () => void {
  let timer: number;
  return () => {
    clearTimeout(timer);
    timer = window.setTimeout(f, timeout);
  };
}

export default AutoRequest;
